import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { identityReviewService, IdentityReviewDto } from '../../../services/identityReview.service';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';

/**
 * D1 — Identity-Review ops console (BRD §4). Lists identity relationships parked as IDENTITY_REVIEW
 * (a 2nd-SP introduction matched an existing investor by email but the introduced NAME conflicts) and
 * lets a compliance operator resolve each:
 *   Clear  → PENDING  (same person; the investor then confirms the link at sign-in)
 *   Reject → DECLINED (different person; no link is created)
 */
export const AdminIdentityReviewList: React.FC = () => {
  const [rows, setRows] = useState<IdentityReviewDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [processing, setProcessing] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await identityReviewService.list());
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load identity reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resolve = async (row: IdentityReviewDto, action: 'clear' | 'reject') => {
    const note = notes[row.id]?.trim() || undefined;
    if (
      action === 'reject' &&
      !globalThis.confirm(
        `Reject the introduction "${row.productName || 'this product'}" as a DIFFERENT person? No link will be created.`
      )
    ) {
      return;
    }
    setProcessing(row.id);
    try {
      if (action === 'clear') {
        await identityReviewService.clear(row.id, note);
        toast.success('Cleared — the investor will confirm this link at sign-in.');
      } else {
        await identityReviewService.reject(row.id, note);
        toast.info('Rejected — no link created.');
      }
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to resolve');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-[18px] font-bold text-slate-800 mb-0">Identity Review</h1>
          <p className="text-[12px] text-slate-500 mt-1 mb-0">
            Introductions that matched an existing investor by email but whose name conflicts (BRD §4).
            Clear (same person) to let the investor confirm on sign-in, or Reject (different person).
          </p>
        </div>
        <button
          onClick={load}
          className="px-3 py-1.5 text-[12px] font-semibold text-[#1f4851] border border-[#1f4851] rounded hover:bg-[#1f4851]/5 transition-colors cursor-pointer"
        >
          <i className="bi bi-arrow-clockwise mr-1.5"></i> Refresh
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] px-4 py-10 text-center">
          <i className="bi bi-shield-check text-[28px] text-[#10b981]"></i>
          <div className="text-[13px] font-semibold text-slate-700 mt-2">No identity reviews pending</div>
          <div className="text-[11px] text-slate-400 mt-1">All flagged introductions have been resolved.</div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((row) => (
            <div key={row.id} className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] px-4 py-3">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="bg-[#fff8f0] text-[#b45309] text-[9px] font-extrabold px-2 py-0.5 rounded uppercase tracking-wider border border-[#f59e0b]/30">
                      Identity Review
                    </span>
                    {row.matchedBy && (
                      <span className="text-[10px] text-slate-400">matched by {row.matchedBy.toLowerCase()}</span>
                    )}
                  </div>

                  <div className="text-[13px] font-bold text-slate-800 leading-tight">
                    {row.investorName || 'Unknown investor'}
                    {row.investorUniqueCode ? (
                      <span className="text-[10px] font-normal text-slate-400 ml-2">{row.investorUniqueCode}</span>
                    ) : null}
                  </div>
                  <div className="text-[11px] text-slate-500">{row.investorEmail || '—'}</div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1 mt-2 text-[11px]">
                    <div>
                      <span className="text-slate-400">Introduced for:</span>{' '}
                      <span className="font-semibold text-slate-700">{row.productName || '—'}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Service Provider:</span>{' '}
                      <span className="font-semibold text-slate-700">{row.serviceProviderName || '—'}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Intro code:</span>{' '}
                      <span className="font-semibold text-slate-700">{row.introCode || '—'}</span>
                    </div>
                  </div>

                  {row.reviewNote && (
                    <div className="mt-2 rounded border border-[#f59e0b]/30 bg-[#fff8f0] px-2.5 py-1.5 text-[11px] text-[#92400e]">
                      <i className="bi bi-exclamation-triangle mr-1.5"></i>
                      {row.reviewNote}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2 w-full md:w-64 shrink-0">
                  <input
                    type="text"
                    value={notes[row.id] ?? ''}
                    onChange={(e) => setNotes((prev) => ({ ...prev, [row.id]: e.target.value }))}
                    placeholder="Resolution note (optional)"
                    className="w-full text-[11px] border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:border-[#1f4851]"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => resolve(row, 'clear')}
                      disabled={processing === row.id}
                      className="flex-1 py-1.5 text-[11px] font-semibold text-white bg-[#1f4851] border border-[#1f4851] rounded hover:bg-[#163a42] transition-colors cursor-pointer disabled:opacity-50"
                      title="Verified same person → PENDING (investor confirms on sign-in)"
                    >
                      {processing === row.id ? 'Saving…' : 'Clear (same person)'}
                    </button>
                    <button
                      onClick={() => resolve(row, 'reject')}
                      disabled={processing === row.id}
                      className="flex-1 py-1.5 text-[11px] font-semibold text-[#b91c1c] bg-transparent border border-[#b91c1c]/40 rounded hover:bg-[#b91c1c]/10 transition-colors cursor-pointer disabled:opacity-50"
                      title="Different person → DECLINED (no link created)"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminIdentityReviewList;
